import { useState, useEffect, useCallback, useRef } from 'react';
import { useRelatoriosDashService } from '../../use-cases/relatorios-dash';

interface FilaStatus {
  IdRelatorioFila: number;
  IdRelatorioTipo: number;
  IdRelatorioStatus: number;
  Json: string;
  Inclusao: string;
  Atualizacao?: string;
  Erro?: string;
  statusText: string;
  message: string;
  data?: any[];
  files?: Array<{
    filename: string;
    path: string;
    size: number;
    type: string;
    downloadUrl: string;
  }>;
  totalFiles?: number;
  totalSize?: number;
}

// Máximo de ciclos consecutivos com PROCESSADO+sem arquivo antes de considerar falha
const MAX_PROCESSADO_SEM_ARQUIVO = 800; // 800 × 3s = 2400s = 40 minutos

export const useFilaStatus = (filaId: number | null, interval: number = 3000) => {
  const [status, setStatus] = useState<FilaStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const processadoSemArquivoCyclesRef = useRef<number>(0);

  const { getRelatorioFilaStatus } = useRelatoriosDashService();

  const checkStatus = useCallback(async () => {
    if (!filaId) {
      return;
    }

    try {
      const result = await getRelatorioFilaStatus(filaId);

      if (result.success) {
        const filaData = result.data;

        setStatus(filaData);
        setError(null);

        if (filaData.statusText === 'ERRO') {
          processadoSemArquivoCyclesRef.current = 0;
          setIsPolling(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        } else if (filaData.statusText === 'PROCESSADO') {
          const temArquivo = filaData.files && filaData.files.length > 0;
          if (temArquivo) {
            // Sucesso: arquivo disponível, parar polling
            processadoSemArquivoCyclesRef.current = 0;
            setIsPolling(false);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } else {
            // PROCESSADO mas sem arquivo ainda (geração em background ou falha)
            processadoSemArquivoCyclesRef.current += 1;
            if (processadoSemArquivoCyclesRef.current >= MAX_PROCESSADO_SEM_ARQUIVO) {
              console.warn(`[useFilaStatus] filaId=${filaId} PROCESSADO sem arquivo há ${processadoSemArquivoCyclesRef.current} ciclos. Timeout de geração.`);
              processadoSemArquivoCyclesRef.current = 0;
              setIsTimedOut(true);
              setIsPolling(false);
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
            }
          }
        } else {
          // EM_FILA ou EM_ANDAMENTO: resetar contador
          processadoSemArquivoCyclesRef.current = 0;
        }
      } else {
        setError(result.message || 'Erro ao consultar status da fila');
      }
    } catch (err) {
      console.error('❌ Erro ao verificar status da fila:', err);
      setError('Erro ao consultar status da fila');
    }
  }, [filaId, getRelatorioFilaStatus]);

  const startPolling = useCallback(() => {
    if (!filaId) {
      return;
    }

    if (isPolling) {
      return;
    }

    // Limpar qualquer interval existente
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsPolling(true);
    setError(null);

    // Verificar imediatamente
    checkStatus();

    // Configurar polling
    intervalRef.current = setInterval(() => {
      checkStatus();
    }, interval);

  }, [filaId, isPolling, checkStatus, interval]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Limpar interval quando componente desmontar
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Iniciar polling quando filaId mudar
  useEffect(() => {
    if (filaId) {
      // Resetar estado de timeout ao trocar de filaId
      setIsTimedOut(false);
      processadoSemArquivoCyclesRef.current = 0;

      // Verificar se o status já é final antes de iniciar polling
      if (status && (status.statusText === 'PROCESSADO' || status.statusText === 'ERRO')) {
        return;
      }

      if (!isPolling) {
        startPolling();
      }
    } else {
      stopPolling();
      setStatus(null);
      setError(null);
      setIsTimedOut(false);
      processadoSemArquivoCyclesRef.current = 0;
    }
  }, [filaId, startPolling, stopPolling, status, isPolling]);

  return {
    status,
    isPolling,
    isTimedOut,
    error,
    startPolling,
    stopPolling,
    checkStatus
  };
};
